import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Gift, Bitcoin, Receipt, MessageCircle, Banknote,
  Upload, Send, LogOut, Flame, Loader2, X, Image as ImageIcon,
  Wallet as WalletIcon, CreditCard, Users as UsersIcon, ShieldCheck, Copy,
} from "lucide-react";
import { toast } from "sonner";
import api, { ngn, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "wallet", label: "Wallet", icon: WalletIcon },
  { id: "crypto", label: "Buy / Sell Crypto", icon: Bitcoin },
  { id: "giftcards", label: "Sell Giftcards", icon: Gift },
  { id: "payments", label: "Payment Methods", icon: CreditCard },
  { id: "referrals", label: "Referrals", icon: UsersIcon },
  { id: "kyc", label: "KYC Verification", icon: ShieldCheck },
  { id: "transactions", label: "Transactions", icon: Receipt },
  { id: "chat", label: "Live Chat", icon: MessageCircle },
];

const STATUS_COLORS = {
  pending: "text-amber-400 bg-amber-500/10",
  approved: "text-emerald-400 bg-emerald-500/10",
  completed: "text-emerald-400 bg-emerald-500/10",
  rejected: "text-rose-400 bg-rose-500/10",
};

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [cards, setCards] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [txns, setTxns] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wallet, setWallet] = useState({ balance_ngn: 0, deposits: [] });
  const [pms, setPms] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [kyc, setKyc] = useState({ status: "none" });
  const [wa, setWa] = useState("");

  const loadAll = () => {
    api.get("/giftcards/rates").then((r) => setCards(r.data)).catch(() => {});
    api.get("/crypto/rates").then((r) => setCrypto(r.data)).catch(() => {});
    api.get("/transactions").then((r) => setTxns(r.data)).catch(() => {});
    api.get("/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {});
    api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/payment-methods").then((r) => setPms(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setReferrals(r.data)).catch(() => {});
    api.get("/kyc").then((r) => setKyc(r.data)).catch(() => {});
    api.get("/settings").then((r) => setWa(r.data.whatsapp_number)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="tb-glow min-h-screen flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-blue-900/30 bg-[#0A1026]/80 backdrop-blur-xl lg:fixed lg:top-0 lg:left-0 lg:bottom-0 z-40">
        <div className="p-5 flex items-center gap-2 border-b border-blue-900/30">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center tb-btn-primary"><span className="font-head font-extrabold text-white">T</span></span>
          <span className="font-head font-extrabold text-white">Travis<span className="tb-gradient-text">B</span></span>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`user-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-blue-500/15 text-blue-200" : "text-gray-400 hover:text-white hover:bg-[#17244F]"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
          <button data-testid="user-logout-button" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors">
            <LogOut size={18} /> Logout
          </button>
        </nav>
      </aside>

      <main className="flex-1 lg:ml-64 p-4 sm:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Hi, {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-gray-400 text-sm">Welcome to your TravisB trading dashboard.</p>
          </div>

          {tab === "overview" && <Overview txns={txns} user={user} setTab={setTab} />}
          {tab === "giftcards" && <SellGiftcards cards={cards} wa={wa} onDone={() => { loadAll(); setTab("transactions"); }} />}
          {tab === "crypto" && <TradeCrypto crypto={crypto} onDone={() => { loadAll(); setTab("transactions"); }} />}
          {tab === "transactions" && <Transactions txns={txns} />}
          {tab === "wallet" && <Wallet wallet={wallet} withdrawals={withdrawals} onDone={loadAll} />}
          {tab === "payments" && <PaymentMethods pms={pms} onDone={loadAll} />}
          {tab === "referrals" && <Referrals data={referrals} />}
          {tab === "kyc" && <Kyc kyc={kyc} onDone={loadAll} />}
          {tab === "chat" && <Chat wa={wa} />}
        </div>
      </main>
    </div>
  );
}

function StatusBadge({ status }) {
  return <span className={`text-xs font-semibold rounded-full px-2.5 py-1 capitalize ${STATUS_COLORS[status] || "text-gray-400 bg-gray-500/10"}`}>{status}</span>;
}

function Overview({ txns, user, setTab }) {
  const pending = txns.filter((t) => t.status === "pending").length;
  const total = txns.reduce((a, t) => a + (t.status === "approved" ? t.payout_ngn : 0), 0);
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {[["Account balance", ngn(user?.balance_ngn)], ["Total earned", ngn(total)], ["Pending trades", pending]].map(([l, v]) => (
          <div key={l} className="tb-card p-5"><div className="text-xs uppercase tracking-wider text-gray-500">{l}</div><div className="font-head text-2xl font-extrabold text-white mt-1 font-mono-tb">{v}</div></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <button data-testid="overview-sell-giftcard" onClick={() => setTab("giftcards")} className="tb-card p-6 text-left hover:bg-[#17244F] transition-colors group">
          <Gift size={24} className="text-blue-300 mb-3" />
          <h3 className="font-head font-semibold text-white">Sell a Gift Card</h3>
          <p className="text-sm text-gray-400 mt-1">Upload & get instant NGN payout.</p>
        </button>
        <button data-testid="overview-trade-crypto" onClick={() => setTab("crypto")} className="tb-card p-6 text-left hover:bg-[#17244F] transition-colors">
          <Bitcoin size={24} className="text-blue-300 mb-3" />
          <h3 className="font-head font-semibold text-white">Trade Crypto</h3>
          <p className="text-sm text-gray-400 mt-1">Buy or sell BTC, USDT, ETH & more.</p>
        </button>
      </div>
      <Transactions txns={txns.slice(0, 5)} title="Recent activity" />
    </div>
  );
}

function ImagePicker({ image, setImage, testid }) {
  const ref = useRef();
  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Image must be under 4MB");
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
      {image ? (
        <div className="relative">
          <img src={image} alt="card" className="w-full h-44 object-cover rounded-xl border border-blue-500/25" />
          <button onClick={() => setImage("")} className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5 text-white"><X size={16} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current.click()} className="w-full h-44 rounded-xl border-2 border-dashed border-blue-500/30 flex flex-col items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-300 transition-colors">
          <Upload size={28} className="mb-2" /> <span className="text-sm">Click to upload card image</span>
        </button>
      )}
    </div>
  );
}

function SellGiftcards({ cards, wa, onDone }) {
  const [brand, setBrand] = useState("");
  const [value, setValue] = useState("");
  const [image, setImage] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const selected = cards.find((c) => c.brand === brand);
  const payout = selected && value ? selected.rate_ngn * parseFloat(value || 0) : 0;

  const submit = async () => {
    if (!brand || !value) return toast.error("Select a card and enter value");
    setLoading(true);
    try {
      await api.post("/trades/giftcard", { brand, card_value: parseFloat(value), currency: selected?.category || "USD", image, notes });
      toast.success("Trade submitted! Our team will review it shortly.");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const waText = encodeURIComponent(`Hi TravisB! I want to sell a ${brand || "gift card"} worth ${value || ""} ${selected?.category || "USD"}. Expected payout: ${ngn(payout)}.`);
  const waLink = `https://wa.me/${wa}?text=${waText}`;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tb-card p-6 space-y-4">
        <h2 className="font-head font-semibold text-white text-lg">Sell a Gift Card</h2>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Card brand</label>
          <select data-testid="giftcard-brand-select" value={brand} onChange={(e) => setBrand(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400">
            <option value="">Select a card…</option>
            {cards.map((c) => <option key={c.id} value={c.brand}>{c.brand} — {ngn(c.rate_ngn)}/{c.category}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Card value ({selected?.category || "USD"})</label>
          <input data-testid="giftcard-value-input" type="number" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" placeholder="100" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Card image / e-code screenshot</label>
          <div className="mt-1.5"><ImagePicker image={image} setImage={setImage} testid="giftcard-upload-input" /></div>
        </div>
        <textarea data-testid="giftcard-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes (card type, e-code, etc.)" rows={2} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400 text-sm" />
      </div>

      <div className="tb-card p-6 flex flex-col">
        <h3 className="font-head font-semibold text-white mb-4">Payout summary</h3>
        <div className="space-y-3 text-sm">
          <Row l="Card" v={brand || "—"} />
          <Row l="Value" v={value ? `${value} ${selected?.category || "USD"}` : "—"} />
          <Row l="Rate" v={selected ? `${ngn(selected.rate_ngn)} / ${selected.category}` : "—"} />
          <div className="border-t border-blue-900/30 pt-3 flex justify-between items-center">
            <span className="text-gray-400">You receive</span>
            <span className="font-mono-tb font-extrabold text-emerald-400 text-2xl">{ngn(payout)}</span>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <button data-testid="giftcard-submit-button" onClick={submit} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">
            {loading && <Loader2 size={18} className="animate-spin" />} Submit Trade
          </button>
          <a data-testid="giftcard-whatsapp-button" href={waLink} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white" style={{ background: "#22C55E" }}>
            <MessageCircle size={18} /> Send on WhatsApp
          </a>
          <p className="text-xs text-gray-500 text-center">Submit in-app for tracking, or reach our vendor directly on WhatsApp.</p>
        </div>
      </div>
    </div>
  );
}

function TradeCrypto({ crypto, onDone }) {
  const [coin, setCoin] = useState("");
  const [side, setSide] = useState("sell");
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [loading, setLoading] = useState(false);
  const selected = crypto.find((c) => c.coin === coin);
  const total = selected && amount ? selected.price_ngn * parseFloat(amount || 0) : 0;

  const submit = async () => {
    if (!coin || !amount) return toast.error("Select a coin and amount");
    setLoading(true);
    try {
      await api.post("/trades/crypto", { coin, side, amount_crypto: parseFloat(amount), wallet_address: wallet });
      toast.success("Crypto order submitted!");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tb-card p-6 space-y-4">
        <h2 className="font-head font-semibold text-white text-lg">Trade Crypto</h2>
        <div className="flex gap-2">
          {["sell", "buy"].map((s) => (
            <button key={s} data-testid={`crypto-side-${s}`} onClick={() => setSide(s)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${side === s ? (s === "sell" ? "bg-rose-500/20 text-rose-300 border border-rose-500/40" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40") : "bg-[#0A1026] text-gray-400 border border-blue-500/20"}`}>{s}</button>
          ))}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Coin</label>
          <select data-testid="crypto-coin-select" value={coin} onChange={(e) => setCoin(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400">
            <option value="">Select coin…</option>
            {crypto.map((c) => <option key={c.coin} value={c.coin}>{c.name} ({c.symbol}) — {ngn(c.price_ngn)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Amount ({selected?.symbol || "crypto"})</label>
          <input data-testid="crypto-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" placeholder="0.00" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-400">Wallet address {side === "buy" ? "(your receiving wallet)" : "(for sending)"}</label>
          <input data-testid="crypto-wallet-input" value={wallet} onChange={(e) => setWallet(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400 font-mono-tb text-sm" placeholder="0x… / bc1…" />
        </div>
      </div>
      <div className="tb-card p-6 flex flex-col">
        <h3 className="font-head font-semibold text-white mb-4">Order summary</h3>
        <div className="space-y-3 text-sm">
          <Row l="Type" v={side.toUpperCase()} />
          <Row l="Asset" v={selected ? `${selected.name} (${selected.symbol})` : "—"} />
          <Row l="Rate" v={selected ? ngn(selected.price_ngn) : "—"} />
          <Row l="Amount" v={amount ? `${amount} ${selected?.symbol || ""}` : "—"} />
          <div className="border-t border-blue-900/30 pt-3 flex justify-between items-center">
            <span className="text-gray-400">{side === "sell" ? "You receive" : "You pay"}</span>
            <span className="font-mono-tb font-extrabold text-emerald-400 text-2xl">{ngn(total)}</span>
          </div>
        </div>
        <button data-testid="crypto-submit-button" onClick={submit} disabled={loading} className="mt-6 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">
          {loading && <Loader2 size={18} className="animate-spin" />} Place Order
        </button>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return <div className="flex justify-between"><span className="text-gray-400">{l}</span><span className="text-white font-medium text-right">{v}</span></div>;
}

function Transactions({ txns, title = "Your transactions" }) {
  return (
    <div className="tb-card p-6">
      <h2 className="font-head font-semibold text-white text-lg mb-4">{title}</h2>
      {txns.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">No transactions yet.</p>
      ) : (
        <div className="space-y-3">
          {txns.map((t) => (
            <div key={t.id} data-testid={`txn-row-${t.id}`} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-200">{t.type === "crypto" ? <Bitcoin size={18} /> : <Gift size={18} />}</span>
                <div>
                  <div className="text-sm font-semibold text-white">{t.type === "crypto" ? `${t.side?.toUpperCase()} ${t.symbol}` : t.brand}</div>
                  <div className="text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString()} · {t.type}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono-tb font-bold text-emerald-400 text-sm">{ngn(t.payout_ngn)}</div>
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Withdraw({ withdrawals, onDone }) {
  const [form, setForm] = useState({ amount_ngn: "", bank_name: "", account_number: "", account_name: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.amount_ngn || !form.bank_name || !form.account_number) return toast.error("Fill all fields");
    setLoading(true);
    try {
      await api.post("/withdrawals", { ...form, amount_ngn: parseFloat(form.amount_ngn) });
      toast.success("Withdrawal requested!");
      setForm({ amount_ngn: "", bank_name: "", account_number: "", account_name: "" });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tb-card p-6 space-y-4">
        <h2 className="font-head font-semibold text-white text-lg">Request Withdrawal</h2>
        <input data-testid="withdraw-amount-input" type="number" placeholder="Amount (NGN)" value={form.amount_ngn} onChange={set("amount_ngn")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" />
        <input data-testid="withdraw-bank-input" placeholder="Bank name" value={form.bank_name} onChange={set("bank_name")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" />
        <input data-testid="withdraw-account-number-input" placeholder="Account number" value={form.account_number} onChange={set("account_number")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" />
        <input data-testid="withdraw-account-name-input" placeholder="Account name" value={form.account_name} onChange={set("account_name")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" />
        <button data-testid="withdraw-submit-button" onClick={submit} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">{loading && <Loader2 size={18} className="animate-spin" />} Request Payout</button>
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-4">Withdrawal history</h3>
        {withdrawals.length === 0 ? <p className="text-gray-500 text-sm py-8 text-center">No withdrawals yet.</p> : (
          <div className="space-y-3">
            {withdrawals.map((w) => (
              <div key={w.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20">
                <div><div className="text-sm font-semibold text-white">{w.bank_name}</div><div className="text-xs text-gray-500">{w.account_number}</div></div>
                <div className="text-right"><div className="font-mono-tb font-bold text-white text-sm">{ngn(w.amount_ngn)}</div><StatusBadge status={w.status} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Chat({ wa }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const endRef = useRef();
  const load = () => api.get("/chat").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text && !image) return;
    try {
      await api.post("/chat", { message: text, image });
      setText(""); setImage(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const waLink = `https://wa.me/${wa}?text=${encodeURIComponent("Hi TravisB support!")}`;

  return (
    <div className="tb-card flex flex-col h-[70vh]">
      <div className="p-4 border-b border-blue-900/30 flex items-center justify-between">
        <div className="flex items-center gap-2"><MessageCircle size={18} className="text-blue-300" /><span className="font-head font-semibold text-white">Live Vendor Support</span></div>
        <a href={waLink} target="_blank" rel="noreferrer" data-testid="chat-whatsapp-button" className="flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full text-white" style={{ background: "#22C55E" }}><MessageCircle size={14} /> WhatsApp</a>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 && <p className="text-center text-gray-500 text-sm py-8">Start a conversation with our support team.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${m.sender === "user" ? "tb-btn-primary" : "bg-[#17244F] text-gray-200"}`}>
              {m.image && <img src={m.image} alt="att" className="rounded-lg mb-2 max-h-40" />}
              {m.message && <p className="text-sm">{m.message}</p>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-blue-900/30">
        {image && <div className="mb-2 flex items-center gap-2 text-xs text-blue-300"><ImageIcon size={14} /> Image attached <button onClick={() => setImage("")}><X size={14} /></button></div>}
        <div className="flex items-center gap-2">
          <label className="cursor-pointer text-gray-400 hover:text-blue-300">
            <Upload size={20} />
            <input type="file" accept="image/*" className="hidden" data-testid="chat-image-input" onChange={(e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setImage(r.result); r.readAsDataURL(f); }} />
          </label>
          <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 px-4 py-2.5 rounded-full bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400 text-sm" />
          <button data-testid="chat-send-button" onClick={send} className="w-10 h-10 rounded-full flex items-center justify-center tb-btn-primary"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}

function Wallet({ wallet, withdrawals, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState("");
  const [loading, setLoading] = useState(false);
  const deposit = async () => {
    if (!amount) return toast.error("Enter amount");
    setLoading(true);
    try {
      await api.post("/wallet/deposit", { amount_ngn: parseFloat(amount), method, reference, proof_image: proof });
      toast.success("Deposit submitted for approval");
      setAmount(""); setReference(""); setProof(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div className="space-y-6">
      <div className="tb-card p-6 flex items-center justify-between">
        <div><div className="text-xs uppercase tracking-wider text-gray-500">Wallet balance</div>
          <div className="font-head text-3xl font-extrabold text-white mt-1 font-mono-tb" data-testid="wallet-balance">{ngn(wallet.balance_ngn)}</div></div>
        <WalletIcon size={40} className="text-blue-400" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="tb-card p-6 space-y-4">
          <h3 className="font-head font-semibold text-white">Deposit funds</h3>
          <input data-testid="deposit-amount-input" type="number" placeholder="Amount (NGN)" value={amount} onChange={(e)=>setAmount(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
          <select data-testid="deposit-method-select" value={method} onChange={(e)=>setMethod(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none">
            <option value="bank">Bank transfer</option><option value="crypto">Crypto</option>
          </select>
          <input data-testid="deposit-reference-input" placeholder="Payment reference (optional)" value={reference} onChange={(e)=>setReference(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
          <div><label className="text-xs uppercase tracking-wider text-gray-400">Proof of payment</label><div className="mt-1.5"><ImagePicker image={proof} setImage={setProof} testid="deposit-proof-input" /></div></div>
          <button data-testid="deposit-submit-button" onClick={deposit} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">{loading && <Loader2 size={18} className="animate-spin" />} Submit Deposit</button>
        </div>
        <Withdraw withdrawals={withdrawals} onDone={onDone} />
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-4">Deposit history</h3>
        {wallet.deposits?.length ? wallet.deposits.map((d)=>(
          <div key={d.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20 mb-2">
            <div><div className="text-sm text-white font-medium">{ngn(d.amount_ngn)}</div><div className="text-xs text-gray-500">{d.method} · {new Date(d.created_at).toLocaleDateString()}</div></div>
            <StatusBadge status={d.status} />
          </div>
        )) : <p className="text-gray-500 text-sm py-6 text-center">No deposits yet.</p>}
      </div>
    </div>
  );
}

function PaymentMethods({ pms, onDone }) {
  const [type, setType] = useState("bank");
  const [form, setForm] = useState({});
  const set=(k)=>(e)=>setForm({...form,[k]:e.target.value});
  const [loading,setLoading]=useState(false);
  const add = async () => {
    setLoading(true);
    try { await api.post("/payment-methods", { type, ...form }); toast.success("Payment method added"); setForm({}); onDone(); }
    catch(e){ toast.error(formatApiError(e.response?.data?.detail)); } finally{ setLoading(false); }
  };
  const del = async (id) => { try { await api.delete(`/payment-methods/${id}`); onDone(); } catch(e){ toast.error("Failed"); } };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tb-card p-6 space-y-4">
        <h3 className="font-head font-semibold text-white">Add payment method</h3>
        <div className="flex gap-2">
          {["bank","crypto"].map((t)=>(<button key={t} data-testid={`pm-type-${t}`} onClick={()=>setType(t)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize ${type===t?"tb-btn-primary":"bg-[#0A1026] text-gray-400 border border-blue-500/20"}`}>{t}</button>))}
        </div>
        {type==="bank" ? (<>
          <input data-testid="pm-bank-name" placeholder="Bank name" value={form.bank_name||""} onChange={set("bank_name")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
          <input data-testid="pm-account-number" placeholder="Account number" value={form.account_number||""} onChange={set("account_number")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
          <input data-testid="pm-account-name" placeholder="Account name" value={form.account_name||""} onChange={set("account_name")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
        </>) : (<>
          <input data-testid="pm-coin" placeholder="Coin (e.g. USDT, BTC)" value={form.coin||""} onChange={set("coin")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
          <input data-testid="pm-wallet-address" placeholder="Wallet address" value={form.wallet_address||""} onChange={set("wallet_address")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none font-mono-tb text-sm" />
        </>)}
        <button data-testid="pm-add-button" onClick={add} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">{loading && <Loader2 size={18} className="animate-spin" />} Add Method</button>
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-4">Your methods</h3>
        {pms.length? pms.map((p)=>(
          <div key={p.id} data-testid={`pm-row-${p.id}`} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20 mb-2">
            <div className="flex items-center gap-3"><CreditCard size={18} className="text-blue-300" /><div>
              <div className="text-sm text-white font-medium">{p.type==="bank"? p.bank_name : `${p.coin} wallet`}</div>
              <div className="text-xs text-gray-500 font-mono-tb">{p.type==="bank"? `${p.account_number} · ${p.account_name}` : p.wallet_address}</div></div></div>
            <button data-testid={`pm-delete-${p.id}`} onClick={()=>del(p.id)} className="text-rose-400 hover:text-rose-300"><X size={16} /></button>
          </div>
        )) : <p className="text-gray-500 text-sm py-6 text-center">No payment methods yet.</p>}
      </div>
    </div>
  );
}

function Referrals({ data }) {
  if (!data) return <Loader2 className="animate-spin text-blue-300" />;
  const link = `${window.location.origin}/signup?ref=${data.referral_code}`;
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Referral link copied!"); };
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-3 gap-4">
        {[["Your code", data.referral_code],["Referrals", data.count],["Earnings", ngn(data.earnings_ngn)]].map(([l,v])=>(
          <div key={l} className="tb-card p-5"><div className="text-xs uppercase tracking-wider text-gray-500">{l}</div><div className="font-head text-2xl font-extrabold text-white mt-1 font-mono-tb">{v}</div></div>
        ))}
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-3">Share your link</h3>
        <p className="text-sm text-gray-400 mb-3">Earn {ngn(data.bonus_per_referral)} for every friend who joins with your link.</p>
        <div className="flex items-center gap-2">
          <input data-testid="referral-link" readOnly value={link} className="flex-1 px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none text-sm font-mono-tb" />
          <button data-testid="referral-copy-button" onClick={copy} className="px-4 py-3 rounded-xl tb-btn-primary font-semibold flex items-center gap-2"><Copy size={16} /> Copy</button>
        </div>
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-4">Referred users</h3>
        {data.referred?.length ? data.referred.map((u,i)=>(
          <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20 mb-2"><span className="text-sm text-white">{u.name}</span><span className="text-xs text-gray-500">{u.joined? new Date(u.joined).toLocaleDateString():""}</span></div>
        )) : <p className="text-gray-500 text-sm py-6 text-center">No referrals yet. Share your link!</p>}
      </div>
    </div>
  );
}

function Kyc({ kyc, onDone }) {
  const [form, setForm] = useState({ full_name:"", id_type:"NIN", id_number:"" });
  const [idImg, setIdImg] = useState(""); const [selfie, setSelfie] = useState("");
  const [loading, setLoading] = useState(false);
  const set=(k)=>(e)=>setForm({...form,[k]:e.target.value});
  const submit = async () => {
    if (!form.full_name || !form.id_number) return toast.error("Fill all fields");
    setLoading(true);
    try { await api.post("/kyc", { ...form, id_image: idImg, selfie_image: selfie }); toast.success("KYC submitted for review"); onDone(); }
    catch(e){ toast.error(formatApiError(e.response?.data?.detail)); } finally{ setLoading(false); }
  };
  const status = kyc?.status || "none";
  if (status === "pending" || status === "approved" || status === "rejected") {
    return (
      <div className="tb-card p-8 text-center max-w-lg mx-auto" data-testid="kyc-status-card">
        <ShieldCheck size={44} className={`mx-auto mb-4 ${status==="approved"?"text-emerald-400":status==="rejected"?"text-rose-400":"text-amber-400"}`} />
        <h3 className="font-head font-semibold text-white text-lg mb-1 capitalize">KYC {status}</h3>
        <p className="text-sm text-gray-400">{status==="approved"?"Your identity is verified. Full access unlocked.":status==="rejected"?"Your submission was rejected. Please contact support.":"Your documents are under review. We'll notify you shortly."}</p>
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="tb-card p-6 space-y-4">
        <h3 className="font-head font-semibold text-white">Verify your identity</h3>
        <input data-testid="kyc-fullname-input" placeholder="Full legal name" value={form.full_name} onChange={set("full_name")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
        <select data-testid="kyc-idtype-select" value={form.id_type} onChange={set("id_type")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none">
          <option>NIN</option><option>BVN</option><option>Driver's License</option><option>International Passport</option><option>Voter's Card</option>
        </select>
        <input data-testid="kyc-idnumber-input" placeholder="ID number" value={form.id_number} onChange={set("id_number")} className="w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none" />
      </div>
      <div className="tb-card p-6 space-y-4">
        <div><label className="text-xs uppercase tracking-wider text-gray-400">ID document photo</label><div className="mt-1.5"><ImagePicker image={idImg} setImage={setIdImg} testid="kyc-id-image-input" /></div></div>
        <div><label className="text-xs uppercase tracking-wider text-gray-400">Selfie with ID</label><div className="mt-1.5"><ImagePicker image={selfie} setImage={setSelfie} testid="kyc-selfie-input" /></div></div>
        <button data-testid="kyc-submit-button" onClick={submit} disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">{loading && <Loader2 size={18} className="animate-spin" />} Submit for Verification</button>
      </div>
    </div>
  );
}
